import type {
  ChartArtifactPayload,
  ChartDashboardChartSpec,
  ChartDimensionKey,
} from '@cotera/api';
import { dashboardChartToArtifactPayload } from '@cotera/api';

export type DashboardActiveFilters = Partial<
  Record<ChartDimensionKey, string | null>
>;

export function chartBindsDimension(
  bindings: ChartDashboardChartSpec['bindings'],
  dimension: ChartDimensionKey
): boolean {
  return bindings.x === dimension || bindings.series === dimension;
}

function filterIndicesByLabels(
  labels: string[],
  datasets: ChartDashboardChartSpec['data']['datasets'],
  predicate: (label: string, index: number) => boolean
): { labels: string[]; datasets: ChartDashboardChartSpec['data']['datasets'] } {
  const indices: number[] = [];
  labels.forEach((label, index) => {
    if (predicate(label, index)) {
      indices.push(index);
    }
  });
  return {
    labels: indices.map((i) => labels[i] ?? ''),
    datasets: datasets.map((ds) => ({
      label: ds.label,
      values: indices.map((i) => ds.values[i] ?? 0),
    })),
  };
}

/**
 * Applies shared dashboard filters to one chart. Charts that do not bind a
 * dimension are left unchanged for that filter.
 */
export function applyDashboardFilters(
  chart: ChartDashboardChartSpec,
  filters: DashboardActiveFilters
): ChartArtifactPayload {
  let labels = [...chart.data.labels];
  let datasets = chart.data.datasets.map((ds) => ({
    label: ds.label,
    values: [...ds.values],
  }));

  for (const [dimension, value] of Object.entries(filters)) {
    if (value === null || value === undefined || value === '') {
      continue;
    }

    if (chart.bindings.x === dimension) {
      const filtered = filterIndicesByLabels(
        labels,
        datasets,
        (label) => label === value
      );
      labels = filtered.labels;
      datasets = filtered.datasets;
    }

    if (chart.bindings.series === dimension) {
      datasets = datasets.filter((ds) => ds.label === value);
    }
  }

  return dashboardChartToArtifactPayload({
    ...chart,
    data: { labels, datasets },
  });
}

export function toggleDashboardFilter(
  filters: DashboardActiveFilters,
  dimension: ChartDimensionKey,
  value: string
): DashboardActiveFilters {
  const current = filters[dimension];
  if (current === value) {
    return { ...filters, [dimension]: null };
  }
  return { ...filters, [dimension]: value };
}
